/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-len */
import { motion, useInView, useAnimate, stagger } from 'framer-motion';

import { useRef, useState, useEffect } from 'react';
import { useSanitizedTranslation, XumoButton as Button, ImageButton } from 'ui';
import classNames from 'classnames';
import Image from 'next/image';

const Content = ({ item, noOrphans, noMargin, className = '' }) => {
  const { t } = useSanitizedTranslation();
  const [key, value] = Object.entries(item)[0];

  if (!value) return <></>;

  switch (key) {
    case 'heroTitle':
      return <h2 data-i18n={t(value)} className={`type-title m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'title':
      return <h2 data-i18n={t(value)} className={`type-heading-lg m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'heading':
      return <h2 data-i18n={t(value)} className={`type-heading m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'headingAlt':
      return <h2 data-i18n={t(value)} className={`type-heading-alt m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'subheading':
      return <h2 data-i18n={t(value)} className={`type-subheading m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'subheadingAlt':
      return <h2 data-i18n={t(value)} className={`type-subheading-alt m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'subheadingSmall':
      return <h2 data-i18n={t(value)} className={`type-subheading-small m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'subheadingLight':
      return <h2 data-i18n={t(value)} className={`type-subheading-light m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'body':
      return (
        <div
          data-i18n={value}
          className={`content type-body m-0 p-0 ${className}`}
          dangerouslySetInnerHTML={{
            //__html: noOrphans ? t(value.replace(/ (?!.* )/, '&nbsp;').replace(/ (?!.* )/, '&nbsp;')) : t(value),
            __html: t(value),
          }}
        />
      );
    case 'bodySmall':
      return <div data-i18n={t(value)} className={`content type-body-small m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'bodyBase':
      return <div data-i18n={t(value)} className={`content type-body-base m-0 p-0 ${className}`} dangerouslySetInnerHTML={{ __html: t(value) }} />;
    case 'image':
      return (
        <Image
          src={value.src}
          alt={value.alt || ''}
          width={value.width}
          height={value.height}
          className={`img inline-block ${className}`}
          style={{ height: value.rederedWidth ? 'auto' : value.renderedHeight, width: value.rederedWidth ? value.rederedWidth : 'auto' }}
          sizes="100vw"
        />
      );
    case 'ctaRow':
      return (
        <div className={`flex flex-col gap-1 md:flex-row md:gap-4 ${!noMargin ? 'btn-row' : ''} ${className}`}>
          {value.map(button => (button.image ? <ImageButton {...button} /> : <Button {...button} />))}
        </div>
      );
    case 'stackedCtaRow':
      return (
        <div className={`flex flex-col gap-4 2xl:flex-row ${!noMargin ? 'btn-row' : ''} ${className}`}>
          {value.map(button => (button.image ? <ImageButton {...button} /> : <Button {...button} />))}
        </div>
      );
    default:
      return <></>;
  }
  return <></>;
};

export const StackedContent = ({
  content,
  full,
  center,
  mobileCenter,
  light,
  noOrphans,
  margin,
  space,
  minHeight,
  left,
  fullalways,
  fullheight,
  mobileCenterLg,
  noMargin,
  className,
}) => {
  const [ref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: margin || '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        'p, h1, h2, h3, h4, .img, .content, .btn',
        {
          y: [30, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 0.8,
          delay: stagger(0.15),
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  const classes = classNames(
    { 'w-full md:w-3/4': full },
    { 'w-full': fullalways },
    { 'h-full justify-center': fullheight },
    { 'text-center items-center': center },
    { '[&_>_.btn-row]:flex [&_>_.btn-row]:items-center [&_>_.btn-row]:justify-center': center },

    { 'text-left': left },

    { 'max-md:text-center': mobileCenter },
    { 'max-lg:text-center max-lg:[&_>_.btn-row]:flex max-lg:[&_>_.btn-row]:items-center max-lg:[&_>_.btn-row]:justify-center': mobileCenterLg },
    { 'max-md:[&_>_.btn-row]:flex max-md:[&_>_.btn-row]:items-center max-md:[&_>_.btn-row]:justify-center': mobileCenter },

    { 'text-white max-lg:text-center max-lg:[&_>_.btn-row]:flex max-lg:[&_>_.btn-row]:items-center max-lg:[&_>_.btn-row]:justify-center': light },
    // { '[&_>_.btn-row]:-ml-4': !center }, remove this to left align button properly
    { '[&_>_h1]:pb-4 [&_>_h2]:pb-4': true && !noMargin },
    { 'space-y-4': !noMargin },

    { 'flex flex-col gap-5': space },
    className,
  );

  return (
    <div className={`flex ${minHeight ? 'h-max' : 'h-full'} w-full items-center justify-center`}>
      <motion.div
        ref={ref}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: margin || '-30% 0% -30% 0%' }}
        className={classes}
      >
        {content.map(item => (
          <Content item={item} noOrphans={noOrphans} noMargin={noMargin} className={item.className} />
        ))}
      </motion.div>
    </div>
  );
};
